import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { ItemThumbnail } from '../../../components/ItemThumbnail';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  addItemsToGroup,
  selectGroupInventoryItems,
} from '../groupsSlice';
import {
  loadInventoryItemsEnhanced,
  loadScenesPage,
  selectInventoryItemsEnhanced,
  selectBrowseLoading,
  selectHasMore,
  selectIsLoadingMore,
  selectScenesCache,
} from '../../browse/browseSlice';
import type { InventoryItemResponse } from '../../../api/types';
import {
  buildPickerSections,
  buildQuickAddChips,
  type PickerMode,
  type PickerSection,
  type QuickAddChip,
} from './AddItemsToGroupSheet/buildPickerSections';
import { SceneSection } from './AddItemsToGroupSheet/SceneSection';
import { QuickAddChips } from './AddItemsToGroupSheet/QuickAddChips';
import { ViewSwitcher } from './AddItemsToGroupSheet/ViewSwitcher';

/**
 * AddItemsToGroupSheet — FE-10.1.6 hierarchical picker.
 *
 * Replaces the flat scrollable list (FE-10.1.5) with a Hybrid layout:
 *
 *   - **Quick-add chips** at the top (Latest scan / Last 7 days /
 *     All <category>) for one-tap bulk selection of common patterns.
 *   - **Section list** grouped by the active mode (default 'scenes',
 *     fallback 'recent' or 'category' via the ViewSwitcher).
 *   - **Drill-in**: tap a section header to expand its item grid for
 *     individual checkbox toggling.
 *   - **Flat search**: typing in the search field collapses grouping
 *     into a single filtered FlatList across all loaded items.
 *
 * State management:
 *   - `selectedIds: Set<number>` — survives mode switches and search.
 *   - `expandedKeys: Set<string>` — independent per-section expansion.
 *   - `mode: PickerMode` — picked via ViewSwitcher; defaults to 'scenes'.
 *
 * Data fetching (all reused, no new backend):
 *   - `loadInventoryItemsEnhanced` — paginated `/users/inventory`
 *     (powers Recent + Category modes + Quick-add chip computation)
 *   - `loadScenesPage` — paginated `/users/files-with-inventory`
 *     (powers Scenes mode + Quick-add chip computation)
 *   - `addItemsToGroup` — confirms the selection
 *
 * Already-bound items are filtered out at every level (per locked
 * decision in the plan): they never appear in scenes, sections,
 * chips, or the flat search.
 */

interface AddItemsToGroupSheetProps {
  visible: boolean;
  groupId: number;
  groupName: string;
  onClose: () => void;
  /** Called after a successful add — typically the parent re-fetches. */
  onAdded?: (count: number) => void;
}

export const AddItemsToGroupSheet: React.FC<AddItemsToGroupSheetProps> = ({
  visible,
  groupId,
  groupName,
  onClose,
  onAdded,
}) => {
  const dispatch = useAppDispatch();
  const allItems = useAppSelector(selectInventoryItemsEnhanced);
  const inventoryLoading = useAppSelector(selectBrowseLoading);
  const isLoadingMore = useAppSelector(selectIsLoadingMore);
  const hasMore = useAppSelector(selectHasMore);
  const scenesCache = useAppSelector(selectScenesCache);
  const groupItems = useAppSelector(selectGroupInventoryItems(groupId));

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<PickerMode>('scenes');
  const [submitting, setSubmitting] = useState(false);

  /* ── data loading ──────────────────────────────────────────────── */

  // Lazy-load the user's inventory once the sheet opens.
  useEffect(() => {
    if (visible && (!allItems || allItems.length === 0)) {
      dispatch(loadInventoryItemsEnhanced({ refresh: true } as any));
    }
  }, [visible, allItems, dispatch]);

  // Lazy-load scenes once the sheet opens (and we're in or could
  // switch to Scenes mode). Cheap when cache is already hot from
  // Browse / SceneViewerScreen.
  useEffect(() => {
    if (visible && !scenesCache.hasLoaded && !scenesCache.loading) {
      dispatch(loadScenesPage({ mode: 'replace', limit: 20, offset: 0 } as any));
    }
  }, [visible, scenesCache.hasLoaded, scenesCache.loading, dispatch]);

  // Reset selection / search / expansion on each open.
  useEffect(() => {
    if (visible) {
      setSelectedIds(new Set());
      setSearch('');
      setExpandedKeys(new Set());
      setMode('scenes');
    }
  }, [visible]);

  /* ── derived ───────────────────────────────────────────────────── */

  const alreadyBound = useMemo(() => {
    const s = new Set<number>();
    for (const it of groupItems) s.add(it.item_id);
    return s;
  }, [groupItems]);

  const sections = useMemo<PickerSection[]>(
    () =>
      buildPickerSections({
        mode,
        allItems,
        scenes: scenesCache.scenes,
        alreadyBoundIds: alreadyBound,
      }),
    [mode, allItems, scenesCache.scenes, alreadyBound],
  );

  const chips = useMemo<QuickAddChip[]>(
    () =>
      buildQuickAddChips({
        allItems,
        scenes: scenesCache.scenes,
        alreadyBoundIds: alreadyBound,
      }),
    [allItems, scenesCache.scenes, alreadyBound],
  );

  const eligibleFlatItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return (allItems ?? []).filter((it: InventoryItemResponse) => {
      if (!it.id || alreadyBound.has(it.id)) return false;
      const name = (it.item_name ?? '').toLowerCase();
      const cat = (it.item_category ?? '').toLowerCase();
      const tags = (it.tags ?? '').toLowerCase();
      return name.includes(q) || cat.includes(q) || tags.includes(q);
    });
  }, [allItems, alreadyBound, search]);

  const totalEligible = useMemo(() => {
    let n = 0;
    for (const it of allItems ?? []) {
      if (it.id != null && !alreadyBound.has(it.id)) n++;
    }
    return n;
  }, [allItems, alreadyBound]);

  const isSearching = search.trim().length > 0;

  /* ── selection handlers ───────────────────────────────────────── */

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSection = (itemIds: number[], targetState: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (targetState) {
        for (const id of itemIds) next.add(id);
      } else {
        for (const id of itemIds) next.delete(id);
      }
      return next;
    });
  };

  const applyChip = (chip: QuickAddChip) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of chip.itemIds) next.add(id);
      return next;
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ── pagination ───────────────────────────────────────────────── */

  // Recent / Category modes paginate via /users/inventory.
  const handleEndReachedFlat = () => {
    if (inventoryLoading || isLoadingMore || !hasMore) return;
    dispatch(loadInventoryItemsEnhanced({ refresh: false } as any));
  };

  // Scenes mode paginates via /users/files-with-inventory. Each new
  // page extends scenesCache.scenes; the section builder picks up the
  // change automatically via the memo dependency.
  const handleEndReachedScenes = () => {
    if (scenesCache.loading || !scenesCache.hasMore) return;
    dispatch(
      loadScenesPage({
        mode: 'append',
        limit: 20,
        offset: scenesCache.nextOffset,
      } as any),
    );
  };

  /* ── submit ───────────────────────────────────────────────────── */

  const handleSubmit = async () => {
    if (selectedIds.size === 0 || submitting) return;
    setSubmitting(true);
    const ids = Array.from(selectedIds);
    const result = await dispatch(addItemsToGroup({ groupId, itemIds: ids }));
    setSubmitting(false);
    if (addItemsToGroup.fulfilled.match(result)) {
      Toast.show({
        type: 'success',
        text1: 'Items added',
        text2: `${result.payload.added} added to ${groupName}`,
      });
      onAdded?.(result.payload.added);
      onClose();
    } else {
      Toast.show({
        type: 'error',
        text1: 'Failed to add items',
        text2: (result.payload as string) ?? 'Please try again.',
      });
    }
  };

  /* ── render ───────────────────────────────────────────────────── */

  const showInitialLoading =
    !isSearching &&
    ((mode === 'scenes' &&
      scenesCache.loading &&
      scenesCache.scenes.length === 0) ||
      (mode !== 'scenes' &&
        inventoryLoading &&
        (!allItems || allItems.length === 0)));

  const showEmptyState =
    !isSearching &&
    !showInitialLoading &&
    (mode === 'scenes'
      ? sections.length === 0 && scenesCache.hasLoaded
      : sections.length === 0);

  const showSearchEmpty =
    isSearching && eligibleFlatItems.length === 0;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel={`Add items to ${groupName}`}
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="add-items-sheet"
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>Add items</Text>
              <Text style={styles.subtitle} numberOfLines={1}>
                to {groupName}
              </Text>
            </View>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          {/* Search */}
          <View style={styles.searchWrap}>
            <MaterialIcons name="search" size={20} color={fpColors.outline} />
            <TextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search your inventory…"
              placeholderTextColor={fpColors.outline}
              style={styles.searchInput}
              autoCorrect={false}
              testID="picker-search"
            />
            {search ? (
              <Pressable
                onPress={() => setSearch('')}
                hitSlop={8}
                accessibilityLabel="Clear search"
              >
                <MaterialIcons name="close" size={18} color={fpColors.outline} />
              </Pressable>
            ) : null}
          </View>

          {/* Quick-add chips + view-switcher (hidden during search) */}
          {!isSearching ? (
            <>
              <QuickAddChips
                chips={chips}
                selectedIds={selectedIds}
                onApply={applyChip}
              />
              <View style={styles.viewSwitcherRow}>
                <Text style={styles.viewSwitcherCaption}>
                  Pick by section
                </Text>
                <ViewSwitcher current={mode} onChange={setMode} />
              </View>
            </>
          ) : null}

          {/* Selection summary bar */}
          <View style={styles.summaryBar}>
            <Text style={styles.summaryText}>
              {selectedIds.size > 0
                ? `${selectedIds.size} selected`
                : isSearching
                ? `${eligibleFlatItems.length} match${eligibleFlatItems.length === 1 ? '' : 'es'}`
                : // Initial-fetch state: the inventory cache is empty and
                  // a load is in flight. Showing "0+ items available"
                  // here looked broken at first glance — render the
                  // honest "Loading…" until we have at least one row.
                inventoryLoading && (allItems?.length ?? 0) === 0
                ? 'Loading…'
                : `${totalEligible}${hasMore ? '+' : ''} item${totalEligible === 1 ? '' : 's'} available`}
            </Text>
            {selectedIds.size > 0 ? (
              <Pressable
                onPress={() => setSelectedIds(new Set())}
                hitSlop={8}
                accessibilityLabel="Clear selection"
              >
                <Text style={styles.clearLink}>Clear</Text>
              </Pressable>
            ) : null}
          </View>

          {/* Body — branches on search vs grouped vs loading vs empty */}
          {showInitialLoading ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator color={fpColors.primary} />
              <Text style={styles.loadingText}>Loading your items…</Text>
            </View>
          ) : showEmptyState ? (
            <View style={styles.emptyBox}>
              <MaterialIcons
                name="inventory"
                size={28}
                color={fpColors.outline}
              />
              <Text style={styles.emptyTitle}>Nothing to add</Text>
              <Text style={styles.emptyBody}>
                {`Every item you own is already in ${groupName}.`}
              </Text>
            </View>
          ) : isSearching ? (
            showSearchEmpty ? (
              <View style={styles.emptyBox}>
                <MaterialIcons
                  name="search-off"
                  size={28}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyTitle}>No matches</Text>
                <Text style={styles.emptyBody}>
                  Try a different search term.
                </Text>
              </View>
            ) : (
              <FlatList
                data={eligibleFlatItems}
                keyExtractor={(it: any) => String(it.id)}
                style={styles.list}
                contentContainerStyle={{ paddingBottom: 12 }}
                keyboardShouldPersistTaps="handled"
                onEndReached={handleEndReachedFlat}
                onEndReachedThreshold={0.5}
                ListFooterComponent={
                  isLoadingMore ? (
                    <View style={styles.footerLoading}>
                      <ActivityIndicator size="small" color={fpColors.primary} />
                      <Text style={styles.footerLoadingText}>
                        Loading more…
                      </Text>
                    </View>
                  ) : null
                }
                renderItem={({ item }) => (
                  <FlatItemRow
                    item={item}
                    selected={selectedIds.has(item.id as number)}
                    onPress={() => toggle(item.id as number)}
                  />
                )}
              />
            )
          ) : (
            // Grouped view (scenes / recent / category)
            <FlatList
              data={sections}
              keyExtractor={(s) => s.key}
              style={styles.list}
              contentContainerStyle={{ paddingBottom: 12 }}
              keyboardShouldPersistTaps="handled"
              onEndReached={
                mode === 'scenes'
                  ? handleEndReachedScenes
                  : handleEndReachedFlat
              }
              onEndReachedThreshold={0.5}
              ListFooterComponent={
                (mode === 'scenes' && scenesCache.loading) ||
                (mode !== 'scenes' && isLoadingMore) ? (
                  <View style={styles.footerLoading}>
                    <ActivityIndicator size="small" color={fpColors.primary} />
                    <Text style={styles.footerLoadingText}>Loading more…</Text>
                  </View>
                ) : (mode === 'scenes' && !scenesCache.hasMore && sections.length > 0) ||
                  (mode !== 'scenes' && !hasMore && allItems.length > 0) ? (
                  <Text style={styles.footerEnd}>
                    {mode === 'scenes'
                      ? `Showing all ${sections.length} snapshots`
                      : `Showing all ${allItems.length} items`}
                  </Text>
                ) : null
              }
              renderItem={({ item: section }) => (
                <SceneSection
                  section={section}
                  selectedIds={selectedIds}
                  expanded={expandedKeys.has(section.key)}
                  onToggle={toggle}
                  onToggleAll={toggleSection}
                  onExpandToggle={() => toggleExpanded(section.key)}
                />
              )}
            />
          )}

          {/* Footer CTA */}
          <View style={styles.footer}>
            <PrimaryCTA
              label={
                submitting
                  ? 'Adding…'
                  : selectedIds.size === 0
                  ? 'Select items to add'
                  : `Add ${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'}`
              }
              onPress={handleSubmit}
              disabled={selectedIds.size === 0 || submitting}
              loading={submitting}
              iconRight={null}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * FlatItemRow — single row used in the search-active flat list
 * ────────────────────────────────────────────────────────────────────── */

const FlatItemRow: React.FC<{
  item: InventoryItemResponse;
  selected: boolean;
  onPress: () => void;
}> = ({ item, selected, onPress }) => {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={`Select ${item.item_name ?? 'item'}`}
      style={[styles.row, selected && styles.rowSelected]}
      testID={`add-items-row-${item.id}`}
    >
      {/* FE-256 — shared fallback chain instead of inline
          primary_photo_url-only check. */}
      <ItemThumbnail item={item as any} size={44} />
      <View style={styles.rowText}>
        <Text style={styles.rowName} numberOfLines={1}>
          {item.item_name ?? 'Unnamed item'}
        </Text>
        {item.item_category || (item as any).estimated_value ? (
          <Text style={styles.rowMeta} numberOfLines={1}>
            {[
              item.item_category,
              typeof (item as any).estimated_value === 'number'
                ? `$${Math.round((item as any).estimated_value)}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        ) : null}
      </View>
      <MaterialIcons
        name={selected ? 'check-box' : 'check-box-outline-blank'}
        size={22}
        color={selected ? fpColors.primary : fpColors.outline}
      />
    </Pressable>
  );
};

/* ──────────────────────────────────────────────────────────────────────
 * Styles
 * ────────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    minHeight: '60%',
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 2,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 20,
    marginTop: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 10,
  },
  searchInput: {
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
  },
  viewSwitcherRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 4,
  },
  viewSwitcherCaption: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  summaryBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  summaryText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  clearLink: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  list: {
    flex: 1,
    paddingHorizontal: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginHorizontal: 8,
    marginBottom: 6,
  },
  rowSelected: {
    backgroundColor: fpColors.primaryContainer,
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowName: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 15,
    color: fpColors.onSurface,
  },
  rowMeta: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    marginTop: 2,
  },
  loadingBox: {
    paddingVertical: 30,
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  loadingText: {
    fontFamily: 'Inter_400Regular',
    color: fpColors.outline,
  },
  emptyBox: {
    paddingVertical: 30,
    paddingHorizontal: 30,
    alignItems: 'center',
    flex: 1,
  },
  emptyTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 15,
    color: fpColors.onSurface,
    marginTop: 8,
  },
  emptyBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
    marginTop: 4,
    textAlign: 'center',
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  footerLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  footerLoadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.outline,
  },
  footerEnd: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.outline,
    textAlign: 'center',
    paddingVertical: 14,
  },
});

export default AddItemsToGroupSheet;
